
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Target, TrendingDown, Zap, Building, Menu } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { NetZeroOverview } from './NetZeroOverview';
import { TargetManager } from './TargetManager';
import { InitiativeManager } from './InitiativeManager';
import { PathwayTracker } from './PathwayTracker';

export const NetZeroMain = () => {
  const [refreshData, setRefreshData] = useState(0);
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { value: 'overview', label: 'Overview', icon: Target },
    { value: 'targets', label: 'Targets', icon: TrendingDown },
    { value: 'initiatives', label: 'Initiatives', icon: Zap },
    { value: 'tracker', label: 'Progress Tracker', icon: Building },
  ];

  const activeTabLabel = tabs.find(t => t.value === activeTab)?.label || 'Overview';

  const handleDataUpdate = () => {
    setRefreshData(prev => prev + 1);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Net-Zero Pathway Management
          </CardTitle>
          <p className="text-gray-600">
            Set targets, track progress, and manage decarbonization initiatives for your net-zero journey
          </p>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
            {/* Desktop Tabs List */}
            <TabsList className="hidden md:grid w-full grid-cols-4">
              {tabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-2">
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Mobile Tabs Dropdown */}
            <div className="md:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="w-full flex justify-start items-center gap-3 px-4 py-6">
                    <Menu className="h-5 w-5 text-gray-500" />
                    <span className="flex items-center gap-2 border-l pl-3">
                      {React.createElement(tabs.find(t => t.value === activeTab)?.icon || Target, { className: "h-5 w-5" })}
                      <span className="font-semibold">{activeTabLabel}</span>
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-64" align="start">
                  {tabs.map((tab) => (
                    <DropdownMenuItem
                      key={tab.value}
                      onClick={() => setActiveTab(tab.value)}
                      className="flex items-center gap-2 py-3"
                    >
                      <tab.icon className="h-4 w-4" />
                      {tab.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <TabsContent value="overview">
              <NetZeroOverview key={refreshData} />
            </TabsContent>

            <TabsContent value="targets">
              <TargetManager onUpdate={handleDataUpdate} />
            </TabsContent>

            <TabsContent value="initiatives">
              <InitiativeManager onUpdate={handleDataUpdate} />
            </TabsContent>

            <TabsContent value="tracker">
              <PathwayTracker key={refreshData} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};
